import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  CalendarDays,
  Megaphone,
  Inbox,
  Image as ImageIcon,
  LogOut,
  ExternalLink,
  Sparkles,
  ChevronLeft,
} from 'lucide-react';
import { useAdminAuth } from './AuthContext';
import { LOGO_URL } from '../mock';

const items = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, end: true },
  { to: '/admin/pages', label: 'Pages', icon: FileText },
  { to: '/admin/events', label: 'Events', icon: CalendarDays },
  { to: '/admin/news', label: 'News & Announcements', icon: Megaphone },
  { to: '/admin/forms', label: 'Form Submissions', icon: Inbox },
  { to: '/admin/media', label: 'Media Library', icon: ImageIcon },
];

export default function AdminLayout() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    document.documentElement.style.backgroundColor = '#0f0d12';
    return () => { document.documentElement.style.backgroundColor = ''; };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-[#0f0d12] text-white flex">
      {/* Sidebar */}
      <aside
        className={`${collapsed ? 'w-[78px]' : 'w-[260px]'} shrink-0 bg-[#16131a] border-r border-white/[0.06] flex flex-col sticky top-0 h-screen transition-[width] duration-300`}
      >
        <div className="px-4 py-5 flex items-center gap-3 border-b border-white/[0.06]">
          <div className="h-11 w-11 rounded-xl bg-white flex items-center justify-center shadow-[0_4px_16px_-4px_rgba(229,97,76,0.5)] overflow-hidden shrink-0">
            <img src={LOGO_URL} alt="GEMS" className="h-full w-full object-contain p-1" />
          </div>
          {!collapsed && (
            <div className="min-w-0">
              <div className="font-serif-display text-[17px] font-bold leading-tight">
                GEMS <span className="text-[#E5614C] italic">@</span> The Arts
              </div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#5DCAB5] font-semibold">Admin CMS</div>
            </div>
          )}
        </div>

        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 px-3 h-11 rounded-xl text-[14px] font-medium transition-colors ${
                  isActive
                    ? 'bg-[#E5614C] text-white shadow-[0_8px_20px_-8px_rgba(229,97,76,0.7)]'
                    : 'text-white/65 hover:bg-white/[0.06] hover:text-white'
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px] shrink-0" />
              {!collapsed && (
                <span className="flex-1 truncate">{item.label}</span>
              )}
              {!collapsed && item.soon && (
                <span className="text-[9px] font-bold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded bg-white/10 text-white/55">
                  Soon
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/[0.06] space-y-2">
          <Link
            to="/"
            target="_blank"
            className="flex items-center gap-3 px-3 h-10 rounded-xl text-[13px] font-medium text-white/65 hover:bg-white/[0.06] hover:text-white transition-colors"
          >
            <ExternalLink className="h-4 w-4 shrink-0" />
            {!collapsed && <span>View Site</span>}
          </Link>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 h-10 rounded-xl text-[13px] font-medium text-white/65 hover:bg-white/[0.06] hover:text-white transition-colors"
          >
            <LogOut className="h-4 w-4 shrink-0" />
            {!collapsed && <span>Sign out</span>}
          </button>
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="w-full flex items-center justify-center gap-2 h-8 rounded-lg text-[11px] font-semibold text-white/40 hover:text-white/80 transition-colors"
          >
            <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="h-[64px] border-b border-white/[0.06] flex items-center justify-between px-7 sticky top-0 bg-[#0f0d12]/85 backdrop-blur-sm z-10">
          <div className="flex items-center gap-2 text-[13px] text-white/55">
            <Sparkles className="h-3.5 w-3.5 text-[#E5614C]" />
            Welcome back, <span className="text-white font-semibold">{admin?.name || 'Admin'}</span>
          </div>
          <div className="text-[12px] text-white/40">{admin?.email}</div>
        </header>
        <div className="p-7">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
